import { Compass, Sparkles, HeartPulse, ClipboardList, Calendar, Play } from "lucide-react";
import { Reveal } from "./shared";

export function About() {
  return (
    <section id="about" className="bg-brand-surfaceAlt py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal><p className="eyebrow">About Har Ghar Strength</p></Reveal>
        <Reveal delay={80}>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight mt-6 text-brand-ink">
            Coaching that fits into
            <em className="italic text-brand-accent"> your life</em> — not the
            other way around.
          </h2>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-brand-ink2 font-sans">
            Har Ghar Strength was built on one honest belief: getting stronger
            shouldn't require an expensive gym or a two-hour daily commute.
            With the right plan, thoughtful progressions, and a coach who
            actually pays attention, your living room or nearest park is
            enough. This is fitness for real people — busy schedules,
            beginner or returning, working from anywhere.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const BENEFITS = [
  {
    icon: Compass,
    title: "Personal guidance",
    body: "Programs built around your body, your goals and the space you actually have — reviewed and adjusted every week.",
  },
  {
    icon: HeartPulse,
    title: "Train anywhere",
    body: "Home, hotel room, park or rooftop. Bodyweight-first with smart progressions using minimal equipment.",
  },
  {
    icon: Sparkles,
    title: "Sustainable, not extreme",
    body: "No crash diets, no burnout cycles. We build habits and strength you can carry for years — not weeks.",
  },
  {
    icon: ClipboardList,
    title: "Real accountability",
    body: "Weekly check-ins on WhatsApp, form reviews on your videos, and a plan that adapts when life happens.",
  },
];

export function WhyChoose() {
  return (
    <section id="why" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><p className="eyebrow">Why Har Ghar Strength</p></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight mt-6 text-brand-ink">
              A simple, considered approach
              <br />
              to getting <em className="italic text-brand-accent">genuinely</em> stronger.
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-border border border-brand-border">
          {BENEFITS.map((b, i) => (
            <Reveal
              key={b.title}
              delay={i * 80}
              className="bg-brand-bg p-8 md:p-12 group"
            >
              <div className="flex items-start gap-5">
                <div className="mt-1 w-11 h-11 flex items-center justify-center bg-brand-accent/10 text-brand-accent group-hover:bg-brand-accent group-hover:text-white transition-colors">
                  <b.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl md:text-3xl text-brand-ink leading-tight">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-brand-ink2 leading-relaxed font-sans">
                    {b.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  {
    n: "01",
    icon: Calendar,
    title: "Book a free call",
    body: "A short conversation to understand your goals, schedule, injuries, and what has and hasn't worked before.",
  },
  {
    n: "02",
    icon: ClipboardList,
    title: "Get your plan",
    body: "A tailored strength & mobility programme delivered digitally — clear workouts, progressions and simple nutrition guidance.",
  },
  {
    n: "03",
    icon: Play,
    title: "Train & progress",
    body: "Weekly check-ins, form feedback and adjustments so you keep moving forward — from your first push-up to your first pull-up.",
  },
];

export function HowItWorks() {
  return (
    <section id="how" className="bg-brand-surfaceAlt py-24 md:py-32 border-y border-brand-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><p className="eyebrow">How it works</p></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight mt-6 text-brand-ink">
              Three simple steps —
              <br />
              <em className="italic text-brand-accent">no guesswork.</em>
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 100}>
              <div className="border-t border-brand-border pt-8">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-5xl md:text-6xl text-brand-accent">{s.n}</span>
                  <s.icon className="h-6 w-6 text-brand-ink2" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl mt-6 leading-tight text-brand-ink">{s.title}</h3>
                <p className="mt-4 text-brand-ink2 leading-relaxed font-sans">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
